import React, { useState, useEffect, useCallback } from "react";
import { fetchCloudRunVersionsAPI } from "./actions";
import { RefreshCcw } from "lucide-react";
import Spinner from "../../components/misc/Spinner";
import packageJson from '../../../package.json';

export interface ApiService {
  serviceName: string;
  url: string;
  status: string;
  revision: string;
  containerImage: string;
  cpuLimit: string;
  memoryLimit: string;
  minInstances: number;
  maxInstances: number;
  timeoutSeconds: number;
  lastDeployed: string;
  appVersion: string;
}

export interface UiService {
  siteName: string;
  url: string;
  status: string;
  version: string;
  lastDeployed: string;
  revision?: string;
  region?: string;
  cpu?: string;
  memory?: string;
  instances?: number;
}

export interface CloudRunResponse {
  marabotApi: ApiService;
  marabotUi: UiService;
  lastChecked: string;
}

export interface ServiceStatus {
  name: string;
  version: string;
  revision: string;
  region: string;
  cpu: string;
  memory: string;
  lastDeployed: string;
  status: string;
  url: string;
  appVersion: string;
}

export interface DeploymentStatusResponse {
  checkedAt: string;
  services: ServiceStatus[];
}

const formatDate = (iso?: string) => {
  if (!iso) return "N/A";
  const date = new Date(iso);
  return isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
};

const DeploymentStatusTable = () => {
  const [data, setData] = useState<DeploymentStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const transformApiData = (apiData: CloudRunResponse): DeploymentStatusResponse => {
    const services: ServiceStatus[] = [
      {
        name: apiData.marabotApi?.serviceName || "N/A",
        version: apiData.marabotApi?.containerImage?.split(':')[1]?.substring(0, 8) || "N/A",
        revision: apiData.marabotApi?.revision || "N/A",
        region: apiData.marabotApi?.containerImage?.split('-docker.pkg.dev')[0] || "N/A",
        cpu: apiData.marabotApi?.cpuLimit || "N/A",
        memory: apiData.marabotApi?.memoryLimit || "N/A",
        lastDeployed: apiData.marabotApi?.lastDeployed || "N/A",
        status: apiData.marabotApi?.status || "N/A",
        url: apiData.marabotApi?.url || "N/A",
        appVersion: apiData.marabotApi?.appVersion || "N/A"
      },
      {
        name: apiData.marabotUi?.siteName || "N/A",
        version: apiData.marabotUi?.version || "N/A",
        revision: apiData.marabotUi?.revision || "N/A",
        region: apiData.marabotUi?.region || "N/A",
        cpu: apiData.marabotUi?.cpu || "N/A",
        memory: apiData.marabotUi?.memory || "N/A",
        lastDeployed: apiData.marabotUi?.lastDeployed || "N/A",
        status: apiData.marabotUi?.status || "N/A",
        url: apiData.marabotUi?.url || "N/A",
        appVersion: packageJson.version
      }
    ];

    return {
      checkedAt: apiData.lastChecked,
      services
    };
  };

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchCloudRunVersionsAPI();
      const transformedData = transformApiData(response.data as CloudRunResponse);
      setData(transformedData);
    } catch (error) {
      console.error("Failed to fetch Cloud Run versions", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  return (
    <div className="bg-white rounded-2xl  ">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Deployment Status</h2>
          <p className="text-sm text-gray-500">
            Last checked: {data ? formatDate(data.checkedAt) : "Loading..."}
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCcw className={`ml-1 h-5 w-5 inline ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left text-sm">
              <th className="p-3">Service</th>
              <th className="p-3">Status</th>
              <th className="p-3">App Version</th>
              <th className="p-3">Version</th>
              <th className="p-3">Revision</th>
              <th className="p-3 text-center">Region</th>
              <th className="p-3 text-center">CPU</th>
              <th className="p-3 text-center">Memory</th>
              <th className="p-3">Last Deploy</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="p-8 text-center text-gray-500">
                   <Spinner />
                </td>
              </tr>
            ) : !data || !data.services || data.services.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-8 text-center text-gray-500">
                  No deployment data available
                </td>
              </tr>
            ) : data.services.map((service) => (
              <tr
                key={service.name}
                className="border-b hover:bg-gray-50 text-sm"
              >
                <td className="p-3">                 
                    {service.name}
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    service.status === 'Ready' ? 'bg-green-100 text-green-800' :
                    service.status === 'DEPLOY' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {service.status}
                  </span>
                </td>
                <td className="p-3 font-semibold text-purple-600">
                  {service.appVersion}
                </td>
                <td className="p-3 font-mono text-blue-600">
                  {service.version}
                </td>
                <td className="p-3 font-mono">{service.revision}</td>
                <td className="p-3 text-center">{service.region}</td>
                <td className="p-3 text-center">{service.cpu}</td>
                <td className="p-3 text-center">{service.memory}</td>
                <td className="p-3">{formatDate(service.lastDeployed)}</td>
              </tr>
            )) || []}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DeploymentStatusTable;
